import { format, isValid } from "date-fns";

// Flight times are wall-clock values in this zone; the API stores them as typed.
export const FLIGHT_TZ = "Africa/Lagos";

// Serialize picker Dates as typed (no UTC conversion, unlike toISOString()).
export const toLocalDateTime = (d: Date) => format(d, "yyyy-MM-dd'T'HH:mm:ss");
export const toLocalDate = (d: Date) => format(d, "yyyy-MM-dd");

// Splits a stored flight time into "yyyy-MM-dd" and "HH:mm". Naive values are
// read as typed; legacy values with Z/offset were saved as UTC, so convert them
// back to FLIGHT_TZ wall-clock time.
export const splitWallClock = (value?: string | null) => {
  const empty = { date: "", time: "" };
  if (!value) return empty;
  const naive = value.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2})/);
  if (!naive) return empty;
  if (!/(Z|[+-]\d{2}:?\d{2})$/i.test(value)) {
    return { date: naive[1], time: `${naive[2]}:${naive[3]}` };
  }
  const d = new Date(value);
  if (!isValid(d)) return empty;
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", {
      timeZone: FLIGHT_TZ,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    })
      .formatToParts(d)
      .map((p) => [p.type, p.value]),
  );
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    time: `${parts.hour}:${parts.minute}`,
  };
};

export const formatFlightDate = (dateString: string) => {
  if (!dateString || dateString.length !== 6) return "";
  const day = dateString.slice(0, 2);
  const month = dateString.slice(2, 4);
  const year = "20" + dateString.slice(4, 6);

  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  const weekday = date.toLocaleDateString("en-US", { weekday: "long" });
  const monthName = date.toLocaleDateString("en-US", { month: "long" });

  return `${weekday} ${parseInt(day)} ${monthName} ${year}`;
};

export const formatDateWithSlash = (dateString: string) => {
  if (!dateString || dateString.length !== 6) return "";
  return `${dateString.slice(0, 2)}/${dateString.slice(
    2,
    4
  )}/20${dateString.slice(4, 6)}`;
};

export const formatFlightTime = (timeString: string) => {
  if (!timeString || timeString.length !== 4) return "";
  const hours = timeString.slice(0, 2);
  const minutes = timeString.slice(2, 4);
  const hour24 = parseInt(hours);
  const period = hour24 >= 12 ? "pm" : "am";
  const hour12 = hour24 % 12 || 12;
  return `${hour12}:${minutes}${period}`;
};

export const formatNumberShort = (num: number): string => {
  if (num >= 1_000_000) {
    return `${(num / 1_000_000)
      .toLocaleString("en-us")
      .replace(/\.?0+$/, "")}M`;
  }
  if (num >= 1_000) {
    return `${(num / 1_000).toLocaleString("en-us").replace(/\.?0+$/, "")}k`;
  }
  if (num === 0) {
    return num.toLocaleString("en-us");
  }
  return num.toLocaleString("en-us");
};

// Formats an ISO timestamp for display; "—" when missing or unparseable.
export const formatDisplayDate = (value?: string | null, withTime = true) => {
  if (!value) return "—";
  const d = new Date(value);
  return isValid(d) ? format(d, withTime ? "d MMM yyyy, HH:mm" : "d MMM yyyy") : "—";
};

export const formatUsd = (value?: number | null) =>
  `$${Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
